import React from 'react'
import { graphql, Link } from 'gatsby'
import styled from 'styled-components'

import SEO from '../../components/SEO'
import Navbar from '../../components/Navbar'
import SearchBar from '../../components/shared/SearchBar'
import { H1, H4, H5, P } from '../../components/shared/Typography'
import Footer from '../../components/Footer'
import { ItemTags } from '../../components/tutorials/List'
import Pill from '../../components/shared/Pill'

import pluralise from '../../utils/pluralise'

const Container = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px 0;
  width: 100%;
  margin: 0 auto;
  padding: 24px;
  padding-bottom: 48px;

  @media (min-width: ${(p) => p.theme.chameleon.breakpoints.xl}) {
    padding: 24px 0;
  }
`

const Wrapper = styled.div`
  overflow-x: hidden;
`

const IntroSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px 0;
  padding: 24px 0;
  max-width: 768px;
  width: 100%;
`

const AuthorBio = styled(P)`
  text-align: center;
`
const ResultsCount = styled(H5)`
  color: ${(p) => p.theme.chameleon.colors.pusher.mediumPurple};
`

const PostsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  max-width: 1200px;
  width: 100%;
`
const Post = styled(Link)`
  border: 1px solid ${(p) => p.theme.chameleon.colors.pusher.borderColor};
  border-radius: 4px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px 0;

  &:hover {
    color: ${(p) => p.theme.chameleon.colors.pusher.mediumPurple};
  }

  @media (min-width: ${(p) => p.theme.chameleon.breakpoints.md}) {
    flex-basis: calc(30%);
    flex-grow: 1;
    min-width: 368px;
    border: 1px solid ${(p) => p.theme.chameleon.colors.pusher.borderColor};
    border-radius: 4px;
  }
`
const ViewResultLink = styled.span`
  font-weight: 600;
  border-bottom: 2px solid;
  padding-bottom: 1px;
  width: fit-content;
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
  padding: 24px 0;
  text-align: center;
`
const EmptyStateHeading = styled.p`
  font-weight: 600;
`

const Author = ({ data, location }) => {
  const authorDetails = data.strapiAuthor
  const authorPosts = [
    ...data.allStrapiBlogPost.nodes.map((node) => ({ ...node, Type: 'blog' })),
    ...data.allStrapiTutorialPost.nodes.map((node) => ({ ...node, Type: 'tutorials' })),
  ].sort(() => 0.5 - Math.random())

  const totalResultsCount = authorPosts.length

  return (
    <>
      <SEO
        title={`${authorDetails.Name} (${authorDetails.Role}) | Pusher authors`}
        description={`Learn more about ${authorDetails.Name} (${authorDetails.Role}) and check out their work with Pusher`}
      />
      <Navbar isSticky={false} />
      <SearchBar title="Search" location={location} />

      <Wrapper>
        <Container>
          <IntroSection>
            <H1>
              {authorDetails.Name} {`(${authorDetails.Role})`}
            </H1>
            <AuthorBio>{authorDetails.Bio}</AuthorBio>
            <ResultsCount>
              {totalResultsCount} {pluralise('post', totalResultsCount)} from {authorDetails.Name}
            </ResultsCount>
          </IntroSection>

          <PostsContainer>
            {authorPosts.length ? (
              authorPosts.map(
                ({ Type, Slug, Title, Excerpt, MetaDescription, Tags = [], Products = [], Categories = [] }) => {
                  let snippet = Excerpt || MetaDescription
                  snippet = snippet.slice(0, 120)
                  const link = `/${Type}/${Slug}`
                  const TYPE_LINK_TEXT_MAP = {
                    tutorials: 'Follow tutorial',
                    blog: 'Read blog post',
                    docs: 'Read doc',
                  }
                  const viewLinkText = TYPE_LINK_TEXT_MAP[Type]

                  const renderedTags = [...Tags, ...Products, ...Categories]

                  return (
                    <Post key={Slug} to={link}>
                      <Pill $variant="dark" $isRounded={true}>
                        {Type}
                      </Pill>

                      <H4>{Title}</H4>
                      <p>{`${snippet}...`}</p>

                      {renderedTags.length && (
                        <ItemTags>
                          {renderedTags.slice(0, 3).map((tag) => (
                            <Pill key={tag.Slug} $isInverted={true}>
                              {tag.Name}
                            </Pill>
                          ))}
                          {renderedTags.length > 3 && '...'}
                        </ItemTags>
                      )}

                      <ViewResultLink>{viewLinkText}</ViewResultLink>
                    </Post>
                  )
                }
              )
            ) : (
              <EmptyState>
                <EmptyStateHeading>{authorDetails.Name} hasn&apos;t written anything yet</EmptyStateHeading>
                <p>Try browsing other blog posts or tutorial posts</p>
              </EmptyState>
            )}
          </PostsContainer>
        </Container>
      </Wrapper>

      <Footer hasTopMargin={false} />
    </>
  )
}

export const query = graphql`
  query Authors($Slug: String) {
    strapiAuthor(Slug: { eq: $Slug }) {
      Name
      Role
      Bio
      Github
      Twitter
    }
    allStrapiBlogPost(sort: { DatePublished: DESC }, filter: { Authors: { elemMatch: { Slug: { in: [$Slug] } } } }) {
      nodes {
        id
        Slug
        Title
        Excerpt
        MetaDescription
        Tags {
          Name
          Slug
        }
        Categories {
          Name
          Slug
        }
        Body {
          data {
            childMarkdownRemark {
              timeToRead
            }
          }
        }
        FeaturedImage {
          url
          name
        }
      }
    }
    allStrapiTutorialPost(
      sort: { DatePublished: DESC }
      filter: { Authors: { elemMatch: { Slug: { in: [$Slug] } } } }
    ) {
      nodes {
        Slug
        Title
        Excerpt
        MetaDescription
        Products {
          Name
          Slug
        }
        Tags {
          Name
          Slug
        }
        Body {
          data {
            childMarkdownRemark {
              timeToRead
            }
          }
        }
      }
    }
  }
`

export default Author
